import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import useInactivityTimer from '../utils/inactivityTimer';
import { UserToken } from '../types/UserToken';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000;
const TOKEN_CHECK_INTERVAL = 10 * 60 * 1000;

interface AuthContextType {
  tokenKey: string | null;
  userToken: UserToken | null;
  name: string | null;
  email: string | null;
  photoUrl: string | null;
  isBroker: boolean | undefined;
  isAdmin: boolean | undefined;
  isHunter: boolean | undefined
  login: (token: UserToken) => void;
  googleLogin: (token: string, accessToken?: string) => void;
  logout: () => void;
  decodeIdToken: (idToken: string) => void;
  wasLoggedIn: boolean;
  setWasLoggedIn: (loggedIn: boolean) => void;
  googleAccessToken: string | null;
  setGoogleAccessToken: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tokenKey, setTokenKey] = useState<string | null>(localStorage.getItem('authToken'));
  const [userToken, setUserToken] = useState<UserToken | null>(() => {
    const storedToken = localStorage.getItem('userToken');
    return storedToken ? JSON.parse(storedToken) : null;
  });
  const [photoUrl, setPhotoUrl] = useState<string | null>(localStorage.getItem('photoUrl'));
  const [name, setName] = useState<string | null>(localStorage.getItem('username'));
  const [email, setEmail] = useState<string | null>(null);
  const [wasLoggedIn, setWasLoggedIn] = useState<boolean>(!!localStorage.getItem('googleIdToken'));
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(
    localStorage.getItem('google_access_token')
  );
  const [isBroker, setIsBroker] = useState<boolean | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState<boolean | undefined>(undefined);
  const [isHunter, setIsHunter] = useState<boolean | undefined>(undefined);

  const isTokenExpired = useCallback((jwtToken: string | null): boolean => {
    if (!jwtToken) return true;
    try {
      const decoded: any = jwtDecode(jwtToken);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch {
      return true;
    }
  }, []);

  // Función modificada para verificar roles
  const checkUserRoles = useCallback(() => {
    if (userToken?.user?.profiles) {
      // Verificar si tiene rol de broker (roleId === 3)
      const hasBrokerRole = userToken.user.profiles.some((profile) => profile.roleId === 3)
      setIsBroker(hasBrokerRole)

      // Verificar si tiene rol de admin (roleId === 8)
      const hasAdminRole = userToken.user.profiles.some((profile) => profile.roleId === 8)
      setIsAdmin(hasAdminRole)

      // Verificar si tiene rol de admin (roleId === 4)
      const hasHunterRole = userToken.user.profiles.some((profile) => profile.roleId === 1)
      setIsHunter(hasHunterRole)
      
    } else {
      setIsBroker(false)
      setIsAdmin(false)
      setIsHunter(false)
    }
  }, [userToken])

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userToken');
    localStorage.removeItem('photoUrl');
    localStorage.removeItem('username');
    localStorage.removeItem('propertyData');
    localStorage.removeItem('selectedProperty');
    localStorage.removeItem('google_access_token');
    setGoogleAccessToken(null);
    setTokenKey(null);
    setUserToken(null);
    setPhotoUrl(null);
    setName(null);
    setEmail(null);
    setIsBroker(false);
    setIsAdmin(false);
    setIsHunter(false);
  };

  const decodeIdToken = (idToken: string) => {
    try {
      const decoded: any = jwtDecode(idToken);
      setName(decoded.name || '');
      setEmail(decoded.email || '');
    } catch (error) {
      console.error('Error al decodificar el token:', error);
    }
  };

  const login = (token: UserToken) => {
    localStorage.setItem('authToken', token.hashedKey);
    localStorage.setItem('userToken', JSON.stringify(token));
    localStorage.setItem('username', token.user.fullName);
    setName(token.user.fullName);
    localStorage.setItem('photoUrl', token.user.photo);
    setPhotoUrl(token.user.photo);
    setUserToken(token);
    setTokenKey(token.hashedKey);
    checkUserRoles()
  };

  const googleLogin = (googleToken: string, googleAccessToken?: string) => {
  localStorage.setItem('googleIdToken', googleToken);
  setWasLoggedIn(true);
  decodeIdToken(googleToken);

  if (googleAccessToken) {
    localStorage.setItem('google_access_token', googleAccessToken);
    setGoogleAccessToken(googleAccessToken);
  }
};


  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const sessionToken = localStorage.getItem('googleIdToken');
    const savedUsername = localStorage.getItem('username');

    if (savedToken && !isTokenExpired(savedToken)) {
      setTokenKey(savedToken);
      const storedUserToken = localStorage.getItem('userToken');
      if (storedUserToken) {
        const parsedUserToken = JSON.parse(storedUserToken);
        setUserToken(parsedUserToken);
        checkUserRoles();
      }
    } else {
      logout();
    }

    if (sessionToken) {
      decodeIdToken(sessionToken);
    }

    if (savedUsername) {
      setName(savedUsername);
    }

    setWasLoggedIn(!!sessionToken);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenExpired]);

  useEffect(() => {
    if (userToken) {
      checkUserRoles(); 
    }
  }, [userToken, checkUserRoles]);


  useInactivityTimer(INACTIVITY_TIMEOUT, logout);

  useEffect(() => {
    const tokenCheckInterval = setInterval(() => {
      if (isTokenExpired(tokenKey)) {
        logout();
      }
    }, TOKEN_CHECK_INTERVAL);

    return () => clearInterval(tokenCheckInterval);
  }, [tokenKey, isTokenExpired]);

  return (
    <AuthContext.Provider
      value={{
        tokenKey,
        decodeIdToken,
        userToken,
        photoUrl,
        name,
        email,
        wasLoggedIn,
        setWasLoggedIn,
        login,
        googleLogin,
        googleAccessToken,
        setGoogleAccessToken,
        logout,
        isBroker,
        isAdmin,
        isHunter,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};